import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import type { FontSettings } from '@getaccept/lib-shared/src/fonts/types/font';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { SelectEntity } from '@getaccept/lib-shared/src/select-entity/types/select-entity';
import { useToast } from '@getaccept/lib-shared/src/toast';
import type { Entity } from '@getaccept/lib-shared/src/types/entity';
import { computed, ref, watchEffect } from 'vue';
import { defineStore } from 'pinia';
import type { Channel } from '@getaccept/pusher';
import type { Ref } from 'vue';
import type { UiStyling } from '@getaccept/lib-shared/src/branding/types/ui-styling';
import type { EntityTheme } from '@getaccept/lib-shared/src/branding/types/theme';
import { useUIStyling } from '@getaccept/lib-shared/src/branding/composables/ui-styling.composable';
import { BackgroundType } from '@getaccept/lib-shared/src/branding/types/enums/background-type';
import { SelectEntityService } from '../api/select-entity/services/select-entity.service';
import type { UpdateEntityInput } from '../api/entity/types/update-entity-input';
import { EntityService } from '../api/entity/services/entity.service';

export const useEntityStore = defineStore('entity', () => {
  const { toast } = useToast();

  const entities = ref<SelectEntity[]>(null);
  const entity = ref<Entity>({
    logoUrl: '',
    theme: undefined,
  });
  const pusherChannel: Ref<Channel> = ref();

  const isSaving = ref(false);
  const isSuccess = ref(false);
  const isError = ref(false);
  const isLoadEntityError = ref(false);
  const isLoading = ref(false);
  const isLoaded = ref(false);

  const loadEntity = async () => {
    isLoading.value = true;
    isLoadEntityError.value = false;
    try {
      const data = await EntityService.fetchEntity();
      entity.value = data;
      isLoaded.value = true;
    } catch (e) {
      toast.danger(t('could-not-load-entity'));
      console.error('Could not load entity');
      isLoadEntityError.value = true;
    } finally {
      isLoading.value = false;
    }
  };

  const loadEntities = async () => {
    try {
      const data = await SelectEntityService.entities();
      entities.value = data;
    } catch (e) {
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
      toast.danger(t('could-not-load-entities'));
    }
  };

  const selectEntity = async (entityId: string) => {
    try {
      await SelectEntityService.selectEntity(entityId);
      window.open('/', '_self');
    } catch (e) {
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
      toast.danger(t('could-not-load-the-selected-entity'));
    }
  };

  const authEntity = async ({ id, auth }: SelectEntity) => {
    if (auth === 'saml') {
      const samlUrl = await SelectEntityService.getSamlUrl(id);
      window.open(samlUrl, '_self');
      return;
    }

    window.open(`/auth/${auth}/${id}`, '_self');
  };

  const setEntity = (newEntity: Entity) => {
    entity.value = newEntity;
  };
  const updateEntity = async (payload: UpdateEntityInput) => {
    try {
      isSaving.value = true;
      await EntityService.update(payload);
      loadEntity();
      isSuccess.value = true;
      setTimeout(() => (isSuccess.value = false), 1500);
    } catch (e) {
      isError.value = true;
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
    } finally {
      isSaving.value = false;
    }
  };

  const setEntityThemeFontSettings = (fontSettings: FontSettings) => {
    entity.value.theme.fontSettings = fontSettings;
  };

  const entityId = computed(() => entity.value?.id);

  const uiStyling = ref<UiStyling | undefined>(undefined);

  watchEffect(() => {
    if (!entity.value?.theme) {
      return;
    }

    const { uiStyling: styling } = useUIStyling(
      entity.value.theme as Omit<EntityTheme, 'id' | 'entityId' | 'fontSettings'> | null
    );

    uiStyling.value = styling.value;
  });

  const backgroundStyle = computed(() => {
    if (uiStyling.value?.background && entity.value?.theme) {
      const isImageBackground =
        'backgroundType' in entity.value.theme &&
        entity.value.theme.backgroundType === BackgroundType.Image;

      return isImageBackground
        ? `background-image: ${uiStyling.value.background}`
        : `background: ${uiStyling.value.background};`;
    }

    return `background-image: url('/assets/img/background/signing-page-bg.svg');`;
  });

  const builtInThemesIds = ref<string[]>([]);

  return {
    entityId,
    entity,
    loadEntity,
    entities,
    loadEntities,
    selectEntity,
    authEntity,
    setEntity,
    updateEntity,
    isSaving,
    isSuccess,
    isError,
    isLoadEntityError,
    setEntityThemeFontSettings,
    pusherChannel,
    isLoading,
    isLoaded,
    uiStyling,
    backgroundStyle,
    builtInThemesIds,
  };
});
